/**
 * Enhanced Emergency Contacts Component
 * Premium UX, Production-Grade, Crisis Management Focused
 * Comprehensive emergency contact management with prioritization and crisis protocols
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  useTheme,
  Stack,
  Paper,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  alpha,
  Badge,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  addEmergencyContact,
  updateEmergencyContact,
  deleteEmergencyContact,
  fetchEmergencyContacts,
} from '../../features/patients/patientSlice';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
  LocalHospital as HospitalIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  Security as SecurityIcon,
  Call as CallIcon,
  Message as MessageIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

// Styled Components
const StyledCard = styled(Card)(({ theme, priority }) => ({
  height: '100%',
  background: priority === 'high'
    ? alpha(theme.palette.error.main, 0.05)
    : priority === 'medium'
    ? alpha(theme.palette.warning.main, 0.05)
    : alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(10px)',
  border: `2px solid ${
    priority === 'high'
      ? alpha(theme.palette.error.main, 0.3)
      : priority === 'medium'
      ? alpha(theme.palette.warning.main, 0.3)
      : alpha(theme.palette.primary.main, 0.1)
  }`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: 'all 0.3s ease-in-out',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
    borderColor:
      priority === 'high'
        ? theme.palette.error.main
        : priority === 'medium'
        ? theme.palette.warning.main
        : theme.palette.primary.main,
  },
}));

const PriorityBadge = styled(Badge)(({ theme, priority }) => ({
  '& .MuiBadge-badge': {
    backgroundColor:
      priority === 'high'
        ? theme.palette.error.main
        : priority === 'medium'
        ? theme.palette.warning.main
        : theme.palette.info.main,
    color: 'white',
    fontWeight: 600,
  },
}));

const ContactCard = styled(Paper)(({ theme, priority, isPrimary }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${
    isPrimary
      ? alpha(theme.palette.primary.main, 0.5)
      : priority === 'high'
      ? alpha(theme.palette.error.main, 0.3)
      : alpha(theme.palette.primary.main, 0.1)
  }`,
  background: isPrimary
    ? alpha(theme.palette.primary.main, 0.05)
    : priority === 'high'
    ? alpha(theme.palette.error.main, 0.05)
    : alpha(theme.palette.background.paper, 0.8),
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateX(8px)',
    boxShadow: theme.shadows[4],
  },
}));

const RELATIONSHIP_OPTIONS = [
  'Spouse',
  'Parent',
  'Sibling',
  'Child',
  'Friend',
  'Partner',
  'Guardian',
  'Other Family Member',
  'Healthcare Provider',
  'Case Manager',
  'Therapist',
  'Other',
];

const PRIORITY_OPTIONS = [
  { value: 'high', label: 'High Priority', color: 'error' },
  { value: 'medium', label: 'Medium Priority', color: 'warning' },
  { value: 'low', label: 'Low Priority', color: 'info' },
];

const EnhancedEmergencyContacts = ({ patientId, role = 'doctor' }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const patientsState = useSelector((state) => state.patients);
  const emergencyContacts = patientsState?.emergencyContacts?.data || [];
  const loading = patientsState?.emergencyContacts?.loading || false;
  const error = patientsState?.emergencyContacts?.error || null;

  // Local state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    priority: 'medium',
    isPrimary: false,
    contactType: 'home', // home, work, mobile
    availability: '24/7', // 24/7, business_hours, evenings, weekends
    preferredContactMethod: 'phone', // phone, email, text
    canMakeDecisions: false,
    hasMedicalPower: false,
    accessCode: '', // For secure contact
  });

  // Filtered contacts
  const filteredContacts = useMemo(() => {
    let filtered = [...emergencyContacts];

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        contact =>
          contact.name?.toLowerCase().includes(searchLower) ||
          contact.phone?.toLowerCase().includes(searchLower) ||
          contact.email?.toLowerCase().includes(searchLower) ||
          contact.relationship?.toLowerCase().includes(searchLower)
      );
    }

    if (filterPriority) {
      filtered = filtered.filter(contact => contact.priority === filterPriority);
    }

    // Sort by priority and primary status
    return filtered.sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
    });
  }, [emergencyContacts, searchTerm, filterPriority]);

  // Primary contact
  const primaryContact = useMemo(() => {
    return filteredContacts.find(contact => contact.isPrimary) || filteredContacts[0];
  }, [filteredContacts]);

  // High priority contacts
  const highPriorityContacts = useMemo(() => {
    return filteredContacts.filter(contact => contact.priority === 'high');
  }, [filteredContacts]);

  // Fetch contacts on mount
  useEffect(() => {
    if (patientId) {
      dispatch(fetchEmergencyContacts(patientId));
    }
  }, [dispatch, patientId]);

  // Handlers
  const handleOpenDialog = useCallback((contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        name: contact.name || '',
        relationship: contact.relationship || '',
        phone: contact.phone || '',
        email: contact.email || '',
        address: contact.address || '',
        notes: contact.notes || '',
        priority: contact.priority || 'medium',
        isPrimary: contact.isPrimary || false,
        contactType: contact.contactType || 'home',
        availability: contact.availability || '24/7',
        preferredContactMethod: contact.preferredContactMethod || 'phone',
        canMakeDecisions: contact.canMakeDecisions || false,
        hasMedicalPower: contact.hasMedicalPower || false,
        accessCode: contact.accessCode || '',
      });
    } else {
      setEditingContact(null);
      setFormData({
        name: '',
        relationship: '',
        phone: '',
        email: '',
        address: '',
        notes: '',
        priority: 'medium',
        isPrimary: false,
        contactType: 'home',
        availability: '24/7',
        preferredContactMethod: 'phone',
        canMakeDecisions: false,
        hasMedicalPower: false,
        accessCode: '',
      });
    }
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingContact(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!patientId) {
      alert('Patient ID is required');
      return;
    }

    try {
      // If setting as primary, unset other primary contacts
      if (formData.isPrimary) {
        // This would be handled by the backend
      }

      if (editingContact) {
        await dispatch(
          updateEmergencyContact({
            patientId,
            contactId: editingContact.id,
            contactData: formData,
          })
        ).unwrap();
      } else {
        await dispatch(
          addEmergencyContact({
            patientId,
            contactData: formData,
          })
        ).unwrap();
      }

      handleCloseDialog();
      // Refresh contacts
      await dispatch(fetchEmergencyContacts(patientId));
    } catch (error) {
      console.error('Failed to save emergency contact:', error);
      alert(`Failed to save contact: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`);
    }
  }, [patientId, formData, editingContact, dispatch, handleCloseDialog]);

  const handleDelete = useCallback(async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this emergency contact? This cannot be undone.')) {
      return;
    }

    try {
      await dispatch(
        deleteEmergencyContact({
          patientId,
          contactId,
        })
      ).unwrap();
      // Refresh contacts
      await dispatch(fetchEmergencyContacts(patientId));
    } catch (error) {
      console.error('Failed to delete emergency contact:', error);
      alert(`Failed to delete contact: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`);
    }
  }, [patientId, dispatch]);

  const handleSetPrimary = useCallback(async (contactId) => {
    try {
      // Update contact to be primary
      const contact = emergencyContacts.find(c => c.id === contactId);
      if (contact) {
        await dispatch(
          updateEmergencyContact({
            patientId,
            contactId,
            contactData: { ...contact, isPrimary: true },
          })
        ).unwrap();
        // Unset other primary contacts (backend should handle this)
      }
    } catch (error) {
      console.error('Failed to set primary contact:', error);
    }
  }, [patientId, emergencyContacts, dispatch]);

  const handleCall = (phone) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleEmail = (email) => {
    if (email) {
      window.location.href = `mailto:${email}`;
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  if (loading && emergencyContacts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Emergency Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage emergency contacts and crisis protocols
          </Typography>
        </Box>
        {role === 'doctor' && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            }}
          >
            Add Contact
          </Button>
        )}
      </Box>

      {/* Crisis Alert */}
      {highPriorityContacts.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small">
              View Protocol
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight={600}>
            High Priority Contacts Available
          </Typography>
          <Typography variant="body2">
            {highPriorityContacts.length} high-priority contact{highPriorityContacts.length !== 1 ? 's' : ''} configured for crisis situations.
          </Typography>
        </Alert>
      )}

      {/* Primary Contact Highlight */}
      {primaryContact && (
        <StyledCard priority={primaryContact.priority} isPrimary sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                >
                  <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                    <PersonIcon />
                  </Avatar>
                </Badge>
                <Box>
                  <Chip label="Primary Contact" color="primary" size="small" sx={{ mb: 1 }} />
                  <Typography variant="h6" fontWeight={600}>
                    {primaryContact.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {primaryContact.relationship}
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={1}>
                {primaryContact.phone && (
                  <Tooltip title="Call">
                    <IconButton
                      color="primary"
                      onClick={() => handleCall(primaryContact.phone)}
                    >
                      <CallIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {primaryContact.email && (
                  <Tooltip title="Email">
                    <IconButton
                      color="primary"
                      onClick={() => handleEmail(primaryContact.email)}
                    >
                      <EmailIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {primaryContact.phone && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2">{primaryContact.phone}</Typography>
                  </Box>
                </Grid>
              )}
              {primaryContact.email && (
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2">{primaryContact.email}</Typography>
                  </Box>
                </Grid>
              )}
              {primaryContact.address && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2">{primaryContact.address}</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </StyledCard>
      )}

      {/* Search and Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder="Search contacts by name, phone, email, or relationship..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Priority Filter</InputLabel>
            <Select
              value={filterPriority}
              label="Priority Filter"
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <MenuItem value="">All Priorities</MenuItem>
              {PRIORITY_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Contacts List */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filteredContacts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <HospitalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No Emergency Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm || filterPriority
              ? 'No contacts match your filters'
              : 'Add emergency contacts to ensure proper care during crises'}
          </Typography>
          {role === 'doctor' && !searchTerm && !filterPriority && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add First Contact
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filteredContacts
            .filter(contact => !contact.isPrimary) // Exclude primary (already shown)
            .map((contact, index) => (
              <Grid item xs={12} md={6} key={contact.id || index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ContactCard priority={contact.priority}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
                          {contact.name?.charAt(0) || <PersonIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {contact.name}
                            </Typography>
                            {contact.priority === 'high' && (
                              <Chip
                                icon={<WarningIcon />}
                                label="High Priority"
                                color="error"
                                size="small"
                              />
                            )}
                            {!contact.isPrimary && role === 'doctor' && (
                              <Tooltip title="Set as Primary">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSetPrimary(contact.id)}
                                >
                                  <StarBorderIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {contact.relationship}
                          </Typography>
                        </Box>
                      </Box>
                      {role === 'doctor' && (
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(contact)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(contact.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                    <Divider sx={{ my: 1.5 }} />
                    <Stack spacing={1}>
                      {contact.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.phone}</Typography>
                          {role === 'doctor' && (
                            <IconButton
                              size="small"
                              onClick={() => handleCall(contact.phone)}
                              color="primary"
                            >
                              <CallIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                      {contact.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.email}</Typography>
                          {role === 'doctor' && (
                            <IconButton
                              size="small"
                              onClick={() => handleEmail(contact.email)}
                              color="primary"
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      )}
                      {contact.address && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.address}</Typography>
                        </Box>
                      )}
                      {contact.notes && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          {contact.notes}
                        </Typography>
                      )}
                    </Stack>
                  </ContactCard>
                </motion.div>
              </Grid>
            ))}
        </Grid>
      )}

      {/* Create/Edit Contact Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              {editingContact ? 'Edit Emergency Contact' : 'Add Emergency Contact'}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Relationship</InputLabel>
              <Select
                name="relationship"
                value={formData.relationship}
                label="Relationship"
                onChange={handleChange}
                required
              >
                {RELATIONSHIP_OPTIONS.map(rel => (
                  <MenuItem key={rel} value={rel}>
                    {rel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Priority Level</InputLabel>
              <Select
                name="priority"
                value={formData.priority}
                label="Priority Level"
                onChange={handleChange}
              >
                {PRIORITY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Notes (Special Instructions)"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={3}
              placeholder="Any special instructions or context for this contact..."
            />
            <FormControlLabel
              control={
                <Switch
                  name="isPrimary"
                  checked={formData.isPrimary}
                  onChange={handleChange}
                />
              }
              label="Set as Primary Emergency Contact"
            />
            <FormControlLabel
              control={
                <Switch
                  name="canMakeDecisions"
                  checked={formData.canMakeDecisions}
                  onChange={handleChange}
                />
              }
              label="Can Make Medical Decisions"
            />
            <FormControlLabel
              control={
                <Switch
                  name="hasMedicalPower"
                  checked={formData.hasMedicalPower}
                  onChange={handleChange}
                />
              }
              label="Has Medical Power of Attorney"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, px: 3 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.name || !formData.phone}
          >
            {loading ? <CircularProgress size={20} /> : editingContact ? 'Update' : 'Add Contact'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedEmergencyContacts;

